// Sidebar panels for the detail page: Campaign details, Audience, Integrations.

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AUDIENCE_FILTER_SCHEMA } from '../../utils/audienceFilterSchema';
import { CHANNEL, OFFER } from '../../constants';
import StatusPill from '../dashboard/StatusPill';
import { media } from '../../../../utils/media';

const Col = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${media.lessThan('laptop')} {
    flex-direction: row;
  }
  ${media.lessThan('tablet')} {
    flex-direction: column;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 20px;
  ${media.lessThan('laptop')} {
    width: 100%;
  }
`;

const Title = styled.div`
  font-weight: 600;
  margin-bottom: 12px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px dashed ${({ theme }) => theme.colors.NEU_100};

  &:last-child {
    border-bottom: 0;
  }
`;

const Key = styled.span`
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const Value = styled.span`
  font-weight: 500;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 0;
`;

const Pill = styled.span`
  background: ${({ theme }) => theme.colors.NEU_100};
  color: ${({ theme }) => theme.colors.NEU_600};
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 11px;
`;

const Dot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 6px;
`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const formatFilter = (f) => {
  const label = AUDIENCE_FILTER_SCHEMA[f.type]?.label ?? f.type;
  return label;
};

const DetailSidebar = ({ campaign, theme }) => {
  const { t } = useTranslation();
  const offerLabel =
    campaign?.offer === OFFER.DISCOUNT
      ? `${campaign.discount?.code ?? ''} · ${campaign.discount?.percentage ?? '—'}%`
      : campaign?.offer === OFFER.INVITATION
      ? 'Invitation'
      : 'No offer';

  const mailchimp = campaign?.mailchimp;

  return (
    <Col>
      <Card>
        <Title>{t('campaigns.sidebar.details', 'Campaign details')}</Title>
        <Row>
          <Key>{t('campaigns.sidebar.status', 'Status')}</Key>
          <Value>
            <StatusPill status={campaign.status} theme={theme} />
          </Value>
        </Row>
        <Row>
          <Key>{t('campaigns.sidebar.channels', 'Channels')}</Key>
          <Value>{(campaign.channels ?? []).map((c) => (c === CHANNEL.EMAIL ? 'Email' : 'In-app')).join(' + ')}</Value>
        </Row>
        <Row>
          <Key>{t('campaigns.sidebar.offer', 'Offer')}</Key>
          <Value>{offerLabel}</Value>
        </Row>
        {/* {campaign.template && (
          <Row>
            <Key>{t('campaigns.sidebar.template', 'Template')}</Key>
            <Value style={{ textTransform: 'capitalize' }}>{campaign.template}</Value>
          </Row>
        )} */}
        <Row>
          <Key>{t('campaigns.sidebar.createdBy', 'Created by')}</Key>
          <Value>{campaign.createdBy?.name ?? '—'}</Value>
        </Row>
        <Row>
          <Key>{t('campaigns.sidebar.created', 'Created')}</Key>
          <Value>{formatDate(campaign.createdAt)}</Value>
        </Row>
        {campaign.schedule?.email?.sendAt && (
          <Row>
            <Key>{t('campaigns.sidebar.sentAt', 'Sent at')}</Key>
            <Value>{formatDate(campaign.schedule.email.sendAt)}</Value>
          </Row>
        )}
      </Card>

      <Card>
        <Title>{t('campaigns.sidebar.audience', 'Audience')}</Title>
        <div style={{ fontSize: 13, marginBottom: 10 }}>{t('campaigns.sidebar.filtersUsed', 'Filters used')}</div>
        <Pills>
          {(campaign.audience?.filters ?? []).map((f, i) => (
            <Pill key={i}>{formatFilter(f)}</Pill>
          ))}
        </Pills>
        {campaign.audience?.estimatedReach != null && (
          <Row style={{ marginTop: 10 }}>
            <Key>{t('campaigns.sidebar.recipients', 'Recipients')}</Key>
            <Value>{(campaign.metrics?.recipients ?? campaign.audience.estimatedReach).toLocaleString()}</Value>
          </Row>
        )}
      </Card>

      {mailchimp && (
        <Card>
          <Title>{t('campaigns.sidebar.integrations', 'Integrations')}</Title>
          <Row>
            <Key>{t('campaigns.sidebar.mailchimpList', 'Mailchimp list')}</Key>
            <Value>
              <Dot $color={mailchimp.synced ? theme.colors.SUCCESS_GREEN : theme.colors.NEU_300} />
              {mailchimp.synced ? 'synced' : 'pending'}
            </Value>
          </Row>
          {mailchimp.campaignId && (
            <Row>
              <Key>{t('campaigns.sidebar.mailchimpId', 'Mailchimp ID')}</Key>
              <Value>{mailchimp.campaignId}</Value>
            </Row>
          )}
          {mailchimp.lastSyncAt && (
            <Row>
              <Key>{t('campaigns.sidebar.lastSync', 'Last sync')}</Key>
              <Value>{formatDate(mailchimp.lastSyncAt)}</Value>
            </Row>
          )}
        </Card>
      )}
    </Col>
  );
};

export default DetailSidebar;
